import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const PURPOSE_LABEL = { appointment_reminder: "Appointment reminder", booking_confirmation: "Booking confirmation",
  service_followup: "Service follow-up", customer_callback: "Customer-requested callback", service_billing_clarification: "Service or billing clarification" };
const fmt = (s) => (s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "—");

function ConsentForm({ customerId, mobile, bookings, onDone }) {
  const [ps, setPs] = useState(["appointment_reminder", "booking_confirmation"]);
  const [source, setSource] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [m, setM] = useState(mobile || "");
  const toggle = (p) => setPs(ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]);
  const save = () => api.post("/biz-outbound/consents", { mobile: m, purposes: ps, source, customer_id: customerId || "", booking_id: bookingId })
    .then(() => { toast.success("Consent recorded"); setSource(""); onDone(); }).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="space-y-2 rounded-lg border p-3" data-testid="consent-form">
      {!mobile && <input value={m} onChange={(e) => setM(e.target.value)} placeholder="Customer mobile" className="w-full rounded border px-2 py-1 text-sm" data-testid="consent-mobile" />}
      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(PURPOSE_LABEL).map(([k, v]) => (
          <label key={k} className="flex items-center gap-1"><input type="checkbox" checked={ps.includes(k)} onChange={() => toggle(k)} data-testid={`consent-purpose-${k}`} />{v}</label>))}
      </div>
      <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="How they agreed (e.g. said yes at booking desk, WhatsApp reply)" className="w-full rounded border px-2 py-1 text-sm" data-testid="consent-source" />
      {bookings?.length > 0 && (
        <select value={bookingId} onChange={(e) => setBookingId(e.target.value)} className="w-full rounded border px-2 py-1 text-sm" data-testid="consent-booking">
          <option value="">All bookings (customer-level consent)</option>
          {bookings.map((b) => <option key={b.id} value={b.id}>{b.service_name} · {fmt(b.start_time)}</option>)}
        </select>)}
      <Button size="sm" onClick={save} disabled={!ps.length || source.length < 3} data-testid="consent-save">Record consent</Button>
      <p className="text-[11px] text-slate-500">Explicit consent is valid for at most 7 days (and never past the chosen booking). Only service calls; never promotional.</p>
    </div>
  );
}

export function CallConsentPanel({ customerId, mobile, bookings }) {
  const qc = useQueryClient();
  const key = ["biz-ob-consents", customerId || "all"];
  const q = useQuery({ queryKey: key, queryFn: async () => (await api.get("/biz-outbound/consents", { params: customerId ? { customer_id: customerId, mobile: mobile || "" } : {} })).data, retry: false });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const optOut = (id) => api.post(`/biz-outbound/consents/${id}/opt-out`, { reason: "Customer asked not to be called" })
    .then(() => { toast.success("Opted out and added to do-not-call"); refresh(); }).catch((e) => toast.error(apiErr(e)));
  if (q.error?.response?.status === 404) return <p className="text-sm text-slate-500" data-testid="consent-unavailable">Outbound calling is not available for this business yet.</p>;
  return (
    <div className="space-y-3" data-testid="call-consent-panel">
      <ConsentForm customerId={customerId} mobile={mobile} bookings={bookings} onDone={refresh} />
      <table className="w-full text-xs" data-testid="consent-table">
        <thead><tr className="text-left text-slate-500"><th>Mobile</th><th>Purposes</th><th>Source</th><th>Recorded</th><th>Expires</th><th>Status</th><th /></tr></thead>
        <tbody>{(q.data || []).map((c) => (
          <tr key={c.id} className="border-t" data-testid={`consent-row-${c.id}`}>
            <td>{c.mobile}</td><td>{c.purposes.map((p) => PURPOSE_LABEL[p]).join(", ")}{c.booking_id ? " (one booking)" : ""}</td><td>{c.source}</td>
            <td>{fmt(c.recorded_at)}</td><td>{fmt(c.expires_at)}</td>
            <td data-testid={`consent-state-${c.id}`}>{c.state}{c.do_not_call ? " · do not call" : ""}</td>
            <td>{c.state === "active" && <Button size="sm" variant="outline" onClick={() => optOut(c.id)} data-testid={`consent-optout-${c.id}`}>Opt out</Button>}</td>
          </tr>))}
        </tbody>
      </table>
      {q.data?.length === 0 && <p className="text-xs text-slate-500" data-testid="consent-empty">No call consent recorded.</p>}
    </div>
  );
}
